/**
 * Fatura detay ekranı — **vücut** mertebesi.
 *
 * Ekranın sırası bilinçli: **önce durum, sonra belge, sonra veri.**
 * Kullanıcı bu ekrana çoğunlukla "bu faturaya ne oldu?" diye gelir; kalem
 * dökümü ikinci sorudur. Hatalı faturada hata kutusu en üstte durur.
 *
 * İşlem düğmeleri (tekrar dene, durum yenile) Veriban'ı DOĞRUDAN çağırmaz:
 * sunucu kuyruğa iş atar (src/panel/api.ts). Bu yüzden düğmeye basınca
 * "yapıldı" değil "sıraya alındı" denir — yalan söylenmez.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import {
  ArrowLeft,
  Ban,
  ExternalLink,
  Eye,
  FileCode2,
  Mail,
  MessageSquare,
  RotateCw,
  Send,
  TriangleAlert,
} from 'lucide-react';
import { api, type FaturaDetayi as FaturaDetayiTipi, type FaturaOlayi } from '../lib/api';
import { Kabuk } from '../components/Kabuk';
import { PdfGoruntuleyici } from '../components/PdfGoruntuleyici';
import { Kart, KartBasligi, KartGovdesi } from '../components/ui/Kart';
import { Dugme } from '../components/ui/Dugme';
import { Pencere } from '../components/ui/Pencere';
import { Girdi } from '../components/ui/Girdi';
import { BosDurum, HataKutusu, Yukleniyor } from '../components/Durumlar';
import { DurumRozeti, TipRozeti } from '../components/DurumRozeti';
import { basariBildir, bilgiBildir, hataBildir, onayIste } from '../lib/bildirim';
import { useOturum } from '../oturum';
import { izinVar } from '../lib/yetki';
import {
  adetYaz,
  belgeTipiYaz,
  goreliZaman,
  kimlikTipiYaz,
  oranYaz,
  paraYaz,
  tarihSaatYaz,
  uzunTarihYaz,
  yonYaz,
} from '../lib/format';

/** Alan/değer çifti — **el** mertebesi. Etiket ve değer tek bir birim. */
function AlanCifti({
  etiket,
  deger,
  rakam = false,
  adres = null,
}: {
  etiket: string;
  deger: string | null | undefined;
  rakam?: boolean;
  /** Değer bir dış sayfaya götürüyorsa (Shopify siparişi) bağlantı olur. */
  adres?: string | null;
}) {
  const bos = deger === null || deger === undefined || deger === '';
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs font-medium text-[var(--ink-muted)]">{etiket}</dt>
      <dd className={`text-sm break-words text-[var(--ink)] ${rakam ? 'rakam' : ''}`}>
        {bos ? (
          '—'
        ) : adres ? (
          <a
            href={adres}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline"
            title="Shopify’da aç"
          >
            {deger}
            <ExternalLink size={12} aria-hidden="true" className="-mt-px" />
          </a>
        ) : (
          deger
        )}
      </dd>
    </div>
  );
}

/** Olay geçmişi — **uzuv**. Faturaya ne olduğunun zaman çizgisi. */
function OlayGecmisi({ olaylar }: { olaylar: FaturaOlayi[] }) {
  if (olaylar.length === 0) {
    return (
      <KartGovdesi>
        <p className="text-sm text-[var(--ink-muted)]">Bu fatura için kayıtlı olay yok.</p>
      </KartGovdesi>
    );
  }

  return (
    <KartGovdesi>
      <ol className="flex flex-col gap-3">
        {olaylar.map((olay) => (
          <li key={olay.id} className="flex gap-3 border-b border-[var(--line)] pb-3 last:border-0 last:pb-0">
            {/* Zaman çizgisi noktası — satırın sol kenarına hizalı. */}
            <span
              className="mt-1.5 size-2 shrink-0 rounded-full bg-[var(--line-strong)]"
              aria-hidden="true"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-[var(--ink)]">{olayEtiketi(olay.eventType)}</span>
                <span className="rakam text-xs text-[var(--ink-faint)]" title={tarihSaatYaz(olay.createdAt)}>
                  {goreliZaman(olay.createdAt)}
                </span>
              </div>
              {olay.message && <p className="text-sm break-words text-[var(--ink-muted)]">{olay.message}</p>}
              {olay.fromStatus && olay.toStatus && (
                <p className="text-xs text-[var(--ink-faint)]">
                  {olay.fromStatus} → {olay.toStatus}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </KartGovdesi>
  );
}

/** Olay tiplerinin Türkçe karşılığı — panelde ham kod görünmez. */
function olayEtiketi(tip: string): string {
  const etiketler: Record<string, string> = {
    CREATED: 'Fatura kaydı oluşturuldu',
    XML_BUILT: 'UBL-TR belgesi hazırlandı',
    XSD_VALIDATED: 'GİB şemasına göre doğrulandı',
    SENT: 'Veriban’a gönderildi',
    NUMBER_ASSIGNED: 'Fatura numarası alındı',
    STATUS_REFRESHED: 'Durum sorgulandı',
    DOCUMENT_DOWNLOADED: 'Belge arşive indirildi',
    DOCUMENT_POLL_GAVE_UP: 'Belge takibi bırakıldı',
    ERROR: 'Hata',
    RETRY: 'Tekrar denendi',
    UYARI: 'Uyarı — kontrol edin',
    CANCELLED: 'İptal edildi',
  };
  return etiketler[tip] ?? tip;
}

export function FaturaDetayi() {
  const { kod = '' } = useParams();
  const { kullanici } = useOturum();
  const sorguIstemcisi = useQueryClient();
  const islemYetkisi = izinVar(kullanici, 'fatura.duzenle');
  const belgeYetkisi = izinVar(kullanici, 'belge.goruntule');
  const [pdfAcik, setPdfAcik] = useState(false);
  const [iptalAcik, setIptalAcik] = useState(false);
  const [iptalNedeni, setIptalNedeni] = useState('');

  const { data, error, isPending, refetch } = useQuery({
    queryKey: ['fatura', kod],
    queryFn: () => api.fatura(kod),
  });

  const tazele = () => {
    void sorguIstemcisi.invalidateQueries({ queryKey: ['fatura', kod] });
    void sorguIstemcisi.invalidateQueries({ queryKey: ['faturalar'] });
  };

  const tekrarDene = useMutation({
    mutationFn: () => api.tekrarDene(kod),
    onSuccess: (sonuc) => {
      if (sonuc.enqueued) basariBildir(sonuc.message, 'Sıraya alındı');
      else bilgiBildir(sonuc.message);
      tazele();
    },
    onError: hataBildir,
  });

  const musteriyeBildir = useMutation({
    mutationFn: (kanal: 'MAIL' | 'SMS') => api.musteriyeBildir(kod, kanal),
    onSuccess: (sonuc) => {
      if (sonuc.ok) basariBildir(sonuc.mesaj, 'Müşteriye gönderildi');
      else hataBildir(new Error(sonuc.mesaj));
      tazele();
    },
    onError: hataBildir,
  });

  const bildirimiSor = async (kanal: 'MAIL' | 'SMS', alici: string) => {
    const onay = await onayIste({
      baslik: kanal === 'MAIL' ? 'Müşteriye e-posta gönderilsin mi?' : 'Müşteriye SMS gönderilsin mi?',
      mesaj: `Fatura bildirimi ${alici} adresine gidecek. Gönderim, Gönderim kayıtları ekranında görünür.`,
      onayMetni: 'Gönder',
    });
    if (onay) musteriyeBildir.mutate(kanal);
  };

  const durumYenile = useMutation({
    mutationFn: () => api.durumYenile(kod),
    onSuccess: (sonuc) => {
      if (sonuc.enqueued) basariBildir(sonuc.message, 'Sıraya alındı');
      else bilgiBildir(sonuc.message);
      tazele();
    },
    onError: hataBildir,
  });

  const iptalEt = useMutation({
    mutationFn: () => api.faturaIptal(kod, iptalNedeni),
    onSuccess: (sonuc) => {
      setIptalAcik(false);
      setIptalNedeni('');
      basariBildir(sonuc.message, 'İptal edildi');
      tazele();
    },
    onError: hataBildir,
  });

  const tekrarDenemeyiSor = async (fatura: FaturaDetayiTipi) => {
    const onay = await onayIste({
      baslik: 'Fatura tekrar gönderilsin mi?',
      mesaj:
        `${fatura.uniqueIntegrationCode} numaralı kayıt Veriban’a yeniden gönderilecek. ` +
        'Bu işlem geri alınamaz; Veriban kabul ederse fatura kesilir ve numara alır.',
      onayMetni: 'Evet, gönder',
      tehlikeli: true,
    });
    if (onay) tekrarDene.mutate();
  };

  if (isPending) {
    return (
      <Kabuk baslik="Fatura" altBaslik={kod}>
        <Kart>
          <Yukleniyor satir={6} />
        </Kart>
      </Kabuk>
    );
  }

  if (error !== null || !data) {
    return (
      <Kabuk baslik="Fatura" altBaslik={kod}>
        <HataKutusu hata={error} tekrarDene={() => void refetch()} />
        <Kart>
          <BosDurum
            baslik="Fatura görüntülenemedi"
            aciklama="Kayıt bulunamadı ya da erişim sırasında bir sorun oluştu."
            eylem={
              <Link to="/faturalar">
                <Dugme gorunum="ikincil" boy="sm" ikon={<ArrowLeft size={13} />}>
                  Listeye dön
                </Dugme>
              </Link>
            }
          />
        </Kart>
      </Kabuk>
    );
  }

  // Belge adresi de tekil kodla kurulur (bkz. FaturaListesi'ndeki not).
  const belgeKodu = data.uniqueIntegrationCode;
  const hataliMi = data.status === 'INVALID' || data.status === 'FAILED';
  const sonDenemeBasi = data.events.findIndex((o) => o.eventType === 'RETRY_REQUESTED');
  const guncelUyarilar = (sonDenemeBasi === -1 ? data.events : data.events.slice(0, sonDenemeBasi))
    .filter((o) => o.eventType === 'UYARI')
    .flatMap((o) => (o.message ?? '').split(' | '))
    .filter((m) => m.trim() !== '');

  return (
    <Kabuk
      baslik={data.invoiceNumber ?? 'Numarasız fatura'}
      altBaslik={`${belgeTipiYaz(data.kind)} · ${yonYaz(data.direction)} · ${uzunTarihYaz(data.issueDate)}`}
      eylemler={
        <>
          <Link to="/faturalar">
            <Dugme gorunum="sessiz" boy="sm" ikon={<ArrowLeft size={13} />}>
              Listeye dön
            </Dugme>
          </Link>
          {islemYetkisi && data.invoiceNumber && (
            <Dugme
              gorunum="ikincil"
              boy="sm"
              ikon={<RotateCw size={13} />}
              bekliyor={durumYenile.isPending}
              onClick={() => durumYenile.mutate()}
            >
              Durumu yenile
            </Dugme>
          )}
          {islemYetkisi && data.cancellable && (
            <Dugme gorunum="ikincil" boy="sm" ikon={<Ban size={13} />} onClick={() => setIptalAcik(true)}>
              İptal et
            </Dugme>
          )}
          {islemYetkisi && data.invoiceNumber && !hataliMi && data.buyerEmail && (
            <Dugme
              gorunum="ikincil"
              boy="sm"
              ikon={<Mail size={13} />}
              bekliyor={musteriyeBildir.isPending && musteriyeBildir.variables === 'MAIL'}
              onClick={() => void bildirimiSor('MAIL', data.buyerEmail ?? '')}
            >
              Müşteriye e-posta
            </Dugme>
          )}
          {islemYetkisi && data.invoiceNumber && !hataliMi && data.buyerGsm && (
            <Dugme
              gorunum="ikincil"
              boy="sm"
              ikon={<MessageSquare size={13} />}
              bekliyor={musteriyeBildir.isPending && musteriyeBildir.variables === 'SMS'}
              onClick={() => void bildirimiSor('SMS', data.buyerGsm ?? '')}
            >
              Müşteriye SMS
            </Dugme>
          )}
          {islemYetkisi && hataliMi && (
            <Dugme
              gorunum="birincil"
              boy="sm"
              ikon={<Send size={13} />}
              bekliyor={tekrarDene.isPending}
              onClick={() => void tekrarDenemeyiSor(data)}
            >
              Tekrar gönder
            </Dugme>
          )}
        </>
      }
    >
      {/* Uyarılar: fatura kesildi ama bazı alanlar tahminle/ayar eksiğiyle dolduruldu — kontrol edilmeli.
          Yalnız SON denemenin uyarıları: "tekrar dene" sonrası eski uyarılar geçmişte kalır (olaylar yeniden
          eskiye sıralı; ilk RETRY_REQUESTED'a kadar olanlar güncel denemedir). */}
      {guncelUyarilar.length > 0 && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-[var(--radius-lg)] border border-[var(--state-wait)] bg-[var(--state-wait-soft)] p-4"
        >
          <span className="-mt-px shrink-0 text-[var(--state-wait)]" aria-hidden="true">
            <TriangleAlert size={18} strokeWidth={2} />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <h2 className="text-sm font-semibold text-[var(--ink)]">Kontrol edilmesi gerekenler</h2>
            <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-[var(--ink)]">
              {guncelUyarilar.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Hata en üstte: kullanıcı bu ekrana çoğunlukla "ne oldu?" diye gelir. */}
      {hataliMi && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-[var(--radius-lg)] border border-[var(--state-bad)] bg-[var(--state-bad-soft)] p-4"
        >
          <span className="-mt-px shrink-0 text-[var(--state-bad)]" aria-hidden="true">
            <TriangleAlert size={18} strokeWidth={2} />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <h2 className="text-sm font-semibold text-[var(--state-bad)]">{data.statusLabel}</h2>
            <p className="max-w-[65ch] text-sm text-[var(--ink)]">{data.statusDescription}</p>
            {data.errorMessage && (
              <p className="mt-1 text-sm break-words text-[var(--ink-muted)]">
                <span className="font-medium">Sistemin verdiği bilgi: </span>
                {data.errorMessage}
              </p>
            )}
            {data.attemptCount > 0 && (
              <p className="text-xs text-[var(--ink-faint)]">
                <span className="rakam">{data.attemptCount}</span> deneme yapıldı.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Durum + belgeler */}
      <Kart>
        <KartGovdesi className="flex flex-col gap-4 @container">
          <div className="flex flex-wrap items-center gap-2">
            <DurumRozeti etiket={data.statusLabel} ton={data.statusTone} aciklama={data.statusDescription} />
            <TipRozeti etiket={belgeTipiYaz(data.kind)} />
            {data.direction === 'CREDIT_NOTE' && <TipRozeti etiket="İade faturası" />}
            {data.gibReportStateName && <TipRozeti etiket={`GİB: ${data.gibReportStateName}`} />}
          </div>

          {!hataliMi && (
            <p className="max-w-[65ch] text-sm text-[var(--ink-muted)]">{data.statusDescription}</p>
          )}

          {/* Belge indirme yalnızca `belge.goruntule` izniyle. Sunucu da bu izni
              istiyor (403); düğmeyi gizlemek kullanıcıyı boş denemeden korur. */}
          {!belgeYetkisi ? (
            <p className="border-t border-[var(--line)] pt-4 text-sm text-[var(--ink-muted)]">
              Belge indirme yetkiniz yok. PDF ve imzalı XML için yöneticinizden “belge görüntüleme” yetkisi
              isteyin.
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-2 border-t border-[var(--line)] pt-4">
              {data.hasPdf ? (
                <Dugme gorunum="birincil" ikon={<Eye size={14} />} onClick={() => setPdfAcik(true)}>
                  PDF’i görüntüle
                </Dugme>
              ) : (
                <Dugme disabled title="Veriban belgeyi hazırlayınca sistem otomatik indirir">
                  PDF henüz hazır değil
                </Dugme>
              )}
              {data.hasSignedXml ? (
                <a href={api.belgeAdresi(belgeKodu, 'xml', data.updatedAt)}>
                  <Dugme gorunum="ikincil" ikon={<FileCode2 size={14} />}>
                    İmzalı XML’i indir
                  </Dugme>
                </a>
              ) : (
                <Dugme disabled title="Mali mühürlü XML henüz indirilmedi">
                  İmzalı XML henüz hazır değil
                </Dugme>
              )}
              {!data.hasPdf && data.documentPollCount > 0 && (
                <span className="text-xs text-[var(--ink-faint)]">
                  Belge <span className="rakam">{data.documentPollCount}</span> kez soruldu; hazır olduğunda
                  otomatik inecek.
                </span>
              )}
            </div>
          )}
        </KartGovdesi>
      </Kart>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Kart>
          <KartBasligi baslik="Alıcı" />
          <KartGovdesi>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <AlanCifti etiket="Ünvan / Ad soyad" deger={data.buyerTitle} />
              <AlanCifti
                etiket={kimlikTipiYaz(data.buyerIdentifierType)}
                deger={data.buyerIdentifier}
                rakam
              />
              <AlanCifti etiket="E-posta" deger={data.buyerEmail} />
              <AlanCifti etiket="Telefon" deger={data.buyerGsm} rakam />
            </dl>
          </KartGovdesi>
        </Kart>

        <Kart>
          <KartBasligi baslik="Belge bilgileri" />
          <KartGovdesi>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <AlanCifti etiket="Fatura numarası" deger={data.invoiceNumber} rakam />
              <AlanCifti etiket="ETTN" deger={data.invoiceUuid} rakam />
              <AlanCifti etiket="Entegrasyon kodu" deger={data.uniqueIntegrationCode} rakam />
              <AlanCifti
                etiket="Sipariş numarası"
                deger={data.shopifyOrderNumber}
                adres={data.shopifyAdminUrl}
              />
              <AlanCifti etiket="Düzenleme tarihi" deger={uzunTarihYaz(data.issueDate)} />
              <AlanCifti etiket="Veriban’a gönderim" deger={tarihSaatYaz(data.sentAt)} />
              <AlanCifti etiket="Profil" deger={data.profileId} />
              <AlanCifti etiket="Seri" deger={data.serieCode} rakam />
            </dl>
          </KartGovdesi>
        </Kart>
      </div>

      {/* Kalemler */}
      <Kart>
        <KartBasligi baslik="Fatura kalemleri" aciklama={`${data.lines.length} kalem · tutarlar KDV hariç`} />
        <div className="yatay-kaydir">
          <table className="w-full min-w-[48rem] border-collapse text-left">
            <thead>
              <tr className="border-b border-[var(--line)] bg-[var(--surface-sunken)]">
                <th className="ust-etiket px-3 py-2 font-semibold">#</th>
                <th className="ust-etiket px-3 py-2 font-semibold">Ürün</th>
                <th className="ust-etiket px-3 py-2 text-right font-semibold">Adet</th>
                <th className="ust-etiket px-3 py-2 text-right font-semibold">Birim fiyat</th>
                <th className="ust-etiket px-3 py-2 text-right font-semibold">İskonto</th>
                <th className="ust-etiket px-3 py-2 text-right font-semibold">Tutar</th>
                <th className="ust-etiket px-3 py-2 text-right font-semibold">KDV</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((kalem) => (
                <tr key={kalem.lineNumber} className="border-b border-[var(--line)] last:border-0">
                  <td className="rakam px-3 py-2 text-sm text-[var(--ink-faint)]">{kalem.lineNumber}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col">
                      <span className="text-sm text-[var(--ink)]">{kalem.name}</span>
                      {kalem.sku && (
                        <span className="rakam text-xs text-[var(--ink-faint)]">{kalem.sku}</span>
                      )}
                    </div>
                  </td>
                  <td className="rakam px-3 py-2 text-right text-sm text-[var(--ink)]">
                    {adetYaz(kalem.quantity)}
                  </td>
                  <td className="rakam px-3 py-2 text-right text-sm text-[var(--ink)]">
                    {paraYaz(kalem.unitPrice)}
                  </td>
                  <td className="rakam px-3 py-2 text-right text-sm text-[var(--ink-muted)]">
                    {kalem.discountAmount === '0' ? '—' : paraYaz(kalem.discountAmount)}
                  </td>
                  <td className="rakam px-3 py-2 text-right text-sm font-medium text-[var(--ink)]">
                    {paraYaz(kalem.lineAmount)}
                  </td>
                  <td className="px-3 py-2 text-right text-sm whitespace-nowrap text-[var(--ink)]">
                    <span className="rakam">{paraYaz(kalem.vatAmount)}</span>
                    <span className="ml-1 text-xs text-[var(--ink-faint)]">{oranYaz(kalem.vatRate)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Toplamlar — sağa yaslı, sabit genişlikli rakam. */}
        <div className="flex justify-end border-t border-[var(--line)] p-4">
          <dl className="flex w-full max-w-80 flex-col gap-2">
            <div className="flex justify-between gap-4">
              <dt className="text-sm text-[var(--ink-muted)]">Mal/hizmet toplamı</dt>
              <dd className="rakam text-sm text-[var(--ink)]">{paraYaz(data.lineExtensionAmount)}</dd>
            </div>
            {data.allowanceTotalAmount !== '0' && (
              <div className="flex justify-between gap-4">
                <dt className="text-sm text-[var(--ink-muted)]">İskonto</dt>
                <dd className="rakam text-sm text-[var(--ink)]">−{paraYaz(data.allowanceTotalAmount)}</dd>
              </div>
            )}
            <div className="flex justify-between gap-4">
              <dt className="text-sm text-[var(--ink-muted)]">KDV matrahı</dt>
              <dd className="rakam text-sm text-[var(--ink)]">{paraYaz(data.taxExclusiveAmount)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-sm text-[var(--ink-muted)]">Hesaplanan KDV</dt>
              <dd className="rakam text-sm text-[var(--ink)]">{paraYaz(data.taxTotalAmount)}</dd>
            </div>
            <div className="flex justify-between gap-4 border-t border-[var(--line)] pt-2">
              <dt className="text-sm font-semibold text-[var(--ink)]">Ödenecek tutar</dt>
              <dd className="rakam text-base font-semibold text-[var(--ink)]">
                {paraYaz(data.payableAmount)}{' '}
                <span className="text-sm font-normal text-[var(--ink-muted)]">
                  {data.currency === 'TRY' ? '₺' : data.currency}
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </Kart>

      <Kart>
        <KartBasligi baslik="Olay geçmişi" aciklama="Bu faturaya ne olduğunun zaman çizgisi" />
        <OlayGecmisi olaylar={data.events} />
      </Kart>
      {iptalAcik && data && (
        <Pencere
          acik
          baslik={`${data.invoiceNumber ?? 'Fatura'} — iptal`}
          aciklama="e-Arşiv faturası Veriban'da iptal edilir; GİB'e raporlanmış fatura iptal edilemez, iade faturası kesilir."
          onKapat={() => setIptalAcik(false)}
          altCubuk={
            <>
              <Dugme gorunum="ikincil" onClick={() => setIptalAcik(false)}>
                Vazgeç
              </Dugme>
              <Dugme
                gorunum="birincil"
                bekliyor={iptalEt.isPending}
                disabled={iptalNedeni.trim().length < 3}
                onClick={() => iptalEt.mutate()}
              >
                Evet, iptal et
              </Dugme>
            </>
          }
        >
          <div className="flex flex-col gap-3 text-sm">
            <p className="text-[var(--ink)]">
              Bu işlem geri alınamaz. Fatura <span className="rakam">{data.invoiceNumber}</span> (
              {paraYaz(data.payableAmount)} ₺) Veriban’da iptal edilir; iptal kaydı ve gerekçe denetim izine
              yazılır.
            </p>
            <Girdi
              etiket="İptal gerekçesi"
              value={iptalNedeni}
              onChange={(o) => setIptalNedeni(o.target.value)}
              placeholder="Örn. test siparişi — gerçek satış değil"
            />
          </div>
        </Pencere>
      )}

      {pdfAcik && data && (
        <PdfGoruntuleyici
          adres={api.belgeAdresi(belgeKodu, 'pdf', data.updatedAt)}
          baslik={data.invoiceNumber ?? belgeKodu}
          altBaslik="Fatura PDF’i — arşivdeki kopya"
          onKapat={() => setPdfAcik(false)}
        />
      )}
    </Kabuk>
  );
}
